/**
 * After the app is ready and the browser is idle, warm up caches for things
 * that are too heavy to pull on first paint but commonly needed soon after.
 *
 * Dynamic imports for chart-vendor chunks — Vite/Rollup downloads them and the
 * SW (StaleWhileRevalidate on app-chunks) caches them.
 *
 * TradingView's charting library is not prefetched: the widget builds its
 * frame in memory and only loads hashed files under bundles/, so no fixed URL
 * would warm anything. The SW caches those bundles on the first chart visit.
 *
 * Returns a cancel function that stops the prefetch if the component unmounts
 * before idle fires.
 *
 * IMPORTANT: keep these imports as side-effect-free as possible. We're not
 * mounting anything, just downloading bytes.
 */

const IDLE_TIMEOUT_MS = 5000;

type IdleCb = (deadline?: IdleDeadline) => void;

const requestIdle = (cb: IdleCb): number => {
  if (typeof window === 'undefined') return 0;
  if ('requestIdleCallback' in window) {
    return (window as any).requestIdleCallback(cb, {
      timeout: IDLE_TIMEOUT_MS
    });
  }
  return window.setTimeout(cb, 2000);
};

const cancelIdle = (id: number): void => {
  if (typeof window === 'undefined') return;
  if ('cancelIdleCallback' in window) {
    (window as any).cancelIdleCallback(id);
  } else {
    window.clearTimeout(id);
  }
};

export function prefetchHeavyAssets(): () => void {
  let cancelled = false;

  const idleId = requestIdle(() => {
    if (cancelled) return;

    // Vite chart-vendor chunks. These imports cause the bundler to fetch
    //    the corresponding chunk; the SW's StaleWhileRevalidate caches it.
    //    .catch is silent — failure here is not a user-visible issue.
    Promise.allSettled([
      import('lightweight-charts'),
      import('recharts')
    ]).catch(() => {});
  });

  return () => {
    cancelled = true;
    cancelIdle(idleId);
  };
}
